import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";

import { api, type PreviewResult } from "../../lib/api";
import { cn } from "../../lib/cn";
import { useToast } from "../../components/toast/ToastProvider";
import type { Source, TabKey } from "./types";
import { ParseIcon } from "./icons";
import { SummaryTab } from "./SummaryTab";
import { AssetsTab } from "./AssetsTab";
import { ContainersTab } from "./ContainersTab";
import { PipelinesTab } from "./PipelinesTab";

export function SourcePreviewPage() {
  const [, params] = useRoute("/sources/:id/preview");
  const [, setLocation] = useLocation();
  const id = params?.id ?? "";
  const qc = useQueryClient();
  const { show } = useToast();

  const source = useQuery({
    queryKey: ["sources", id],
    queryFn: () => api.get<Source>(`/api/v1/sources/${encodeURIComponent(id)}`),
    enabled: !!id,
  });

  const preview = useMutation({
    mutationFn: () => api.post<PreviewResult>(`/api/v1/sources/${encodeURIComponent(id)}/preview`),
  });

  const refresh = useMutation({
    mutationFn: () =>
      api.post<{ runId: string }>(`/api/v1/sources/${encodeURIComponent(id)}/refresh`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources", id, "runs"] });
      show("Ingestion started", `${source.data?.name ?? "Source"} is now ingesting.`, "success");
      setLocation(`/sources/${encodeURIComponent(id)}`);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Ingestion failed to start";
      show("Ingestion error", msg, "error");
    },
  });

  useEffect(() => {
    preview.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!id) return null;

  const data = preview.data;
  const isParsing = preview.isPending;
  const hasResult = !!data;

  return (
    <div className="mx-auto w-full max-w-[1200px] px-10 pb-16">
      <header className="pt-12">
        <button
          onClick={() => setLocation(`/sources/${encodeURIComponent(id)}`)}
          className="text-[12px] text-ash hover:text-ink"
        >
          ← Back to source
        </button>
        <div className="mt-3 flex items-end justify-between gap-6">
          <div>
            <div className="eyebrow">Ingestion Preview</div>
            <h1 className="mt-2 font-display text-[40px] font-semibold leading-tight tracking-tightest text-ink">
              {source.data?.name ?? "—"}
            </h1>
            <p className="mt-2 max-w-2xl text-[13.5px] text-ash leading-relaxed">
              Dry-run parse of this source — no artifact is built and nothing is pushed until you
              click <span className="font-medium text-ink">Proceed with Ingestion</span>.
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            {hasResult && (
              <button onClick={() => preview.mutate()} disabled={isParsing} className="btn-secondary">
                {isParsing ? "Parsing…" : "Re-parse"}
              </button>
            )}
            {hasResult && (
              <button onClick={() => refresh.mutate()} disabled={refresh.isPending} className="btn-primary">
                {refresh.isPending ? "Starting…" : "Proceed with Ingestion →"}
              </button>
            )}
          </div>
        </div>
      </header>

      {isParsing && (
        <div className="mt-10 card px-8 py-16 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-paper animate-pulse">
            <ParseIcon />
          </div>
          <p className="font-display text-[17px] font-semibold text-ink">Parsing…</p>
          <p className="mt-2 text-[13.5px] text-ash">Reading the source and extracting metadata.</p>
        </div>
      )}

      {!isParsing && preview.error && (
        <div className="mt-10 card px-8 py-10 text-center">
          <p className="font-display text-[17px] font-semibold text-ink">Parse failed</p>
          <p className="mt-2 text-[13px] text-rose-700">{(preview.error as Error).message}</p>
          <button onClick={() => preview.mutate()} className="mt-6 btn-secondary">Try again</button>
        </div>
      )}

      {hasResult && <PreviewResultView data={data} />}
    </div>
  );
}

function PreviewResultView({ data }: { data: PreviewResult }) {
  const [activeTab, setActiveTab] = useState<TabKey>("summary");
  const errorWarnings = data.warnings.filter((w) => w.severity === "error");
  const warnWarnings = data.warnings.filter((w) => w.severity === "warn");
  const hasPipelines = data.summary.dataFlowCount + data.summary.dataJobCount > 0;

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: "summary", label: "Summary" },
    { key: "datasets", label: "Assets", count: data.summary.datasetCount },
    { key: "containers", label: "Containers", count: data.summary.containerCount },
    ...(hasPipelines
      ? [{ key: "pipelines" as const, label: "Pipelines", count: data.summary.dataFlowCount + data.summary.dataJobCount }]
      : []),
  ];

  return (
    <div className="mt-8 space-y-4">
      {data.truncated && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
          Results were truncated at the preview cap. The full ingestion will process all entities.
        </div>
      )}

      {(errorWarnings.length > 0 || warnWarnings.length > 0) && (
        <div className={cn("rounded-md border px-4 py-3 text-[13px]", errorWarnings.length > 0 ? "border-rose-200 bg-rose-50 text-rose-800" : "border-amber-200 bg-amber-50 text-amber-800")}>
          {errorWarnings.length > 0 && <span className="font-medium">{errorWarnings.length} error{errorWarnings.length !== 1 ? "s" : ""}</span>}
          {errorWarnings.length > 0 && warnWarnings.length > 0 && " · "}
          {warnWarnings.length > 0 && <span className="font-medium">{warnWarnings.length} warning{warnWarnings.length !== 1 ? "s" : ""}</span>}
          {" "}detected — review the Summary tab before proceeding.
        </div>
      )}

      <div className="border-b border-rule">
        <nav className="-mb-px flex gap-0">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={cn(
                "flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-[13px] font-medium transition-colors",
                activeTab === t.key ? "border-accent text-accent" : "border-transparent text-ash hover:text-ink",
              )}
            >
              {t.label}
              {t.count !== undefined && (
                <span className={cn("rounded-full px-1.5 py-0.5 font-mono text-[10.5px]", activeTab === t.key ? "bg-accent/10 text-accent" : "bg-rule text-ash")}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      <div>
        {activeTab === "summary" && <SummaryTab data={data} />}
        {activeTab === "datasets" && <AssetsTab datasets={data.datasets} sourceId={data.sourceId} />}
        {activeTab === "containers" && <ContainersTab containers={data.containers} />}
        {activeTab === "pipelines" && <PipelinesTab flows={data.dataFlows} jobs={data.dataJobs} />}
      </div>
    </div>
  );
}
